import type { ColumnDef } from '@tanstack/react-table';

import { type GetNtfyMessagesQuery } from 'data/__generated__';

import { Badge } from '@/components/ui/badge';
import { dateColumn, multiSelectColumn, textColumn } from 'components/DataTable';

export type NtfyMessageRow = GetNtfyMessagesQuery['ntfyMessages']['messages'][number];

type NtfyMessageFilterInput = {
    searchValue: string;
    priority: string[];
};

/** Échelle ntfy (1 à 5). Le notifier de l'API y range debug → error sur 1 → 4 ; 5 vient d'Alertmanager. */
const PRIORITY_LABELS: Record<number, string> = {
    1: 'Min',
    2: 'Basse',
    3: 'Normale',
    4: 'Haute',
    5: 'Urgente',
};

const PRIORITY_VARIANTS: Record<number, 'secondary' | 'outline' | 'destructive'> = {
    1: 'outline',
    2: 'outline',
    3: 'secondary',
    4: 'destructive',
    5: 'destructive',
};

export const ntfyColumns: ColumnDef<NtfyMessageRow, unknown>[] = [
    dateColumn<NtfyMessageRow, NtfyMessageFilterInput>({
        accessorKey: 'time',
        header: 'Date',
        defaultVisible: true,
    }),
    textColumn<NtfyMessageRow, NtfyMessageFilterInput>({
        accessorKey: 'topic',
        header: 'Topic',
        defaultVisible: true,
    }),
    multiSelectColumn<NtfyMessageRow, NtfyMessageFilterInput>({
        // Le filtre compare des chaînes : la priorité numérique est exposée comme telle.
        id: 'priority',
        accessorFn: (row) => String(row.priority),
        header: 'Priorité',
        filterKey: 'priority',
        defaultVisible: true,
        options: Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label })),
        cell: ({ row }) => (
            <Badge variant={PRIORITY_VARIANTS[row.original.priority] ?? 'secondary'}>
                {PRIORITY_LABELS[row.original.priority] ?? row.original.priority}
            </Badge>
        ),
    }),
    textColumn<NtfyMessageRow, NtfyMessageFilterInput>({
        accessorKey: 'title',
        header: 'Titre',
        defaultVisible: true,
        truncate: false,
    }),
    textColumn<NtfyMessageRow, NtfyMessageFilterInput>({
        accessorKey: 'message',
        header: 'Message',
        defaultVisible: true,
        truncate: false,
        cell: ({ row }) => <span className="whitespace-pre-wrap break-words">{row.original.message}</span>,
    }),
    textColumn<NtfyMessageRow, NtfyMessageFilterInput>({
        id: 'tags',
        accessorFn: (row) => row.tags.join(', '),
        header: 'Tags',
    }),
];
